import { useEffect, useRef, useState } from 'react';
import styles from './TextArea.module.scss';
import ErrorText from '../../Text/ErrorText/ErrorText';
import Box from '../../Layout/Box/Box';

type OnChangeProps<T> = {
  id?: T;
  value?: string;
};

type Resize = 'none' | 'both' | 'horizontal' | 'vertical' | 'block' | 'inline';

export type TextAreaProps<T> = {
  id?: string;
  name: T;
  variant?: 'background' | 'lined' | 'outlinedShadow';
  value?: string;
  width?: string;
  height?: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  error?: string | null;
  showcaseLength?: boolean;
  maxLengthPosition?: 'left' | 'right';
  isActive?: boolean;
  resizeValue?: Resize;
  rows?: number;
  borderColor?: string;
  style?: React.CSSProperties;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>, props: OnChangeProps<T>) => void;
};

const TextArea = <T extends string>(props: TextAreaProps<T>): React.JSX.Element => {
  const {
    id,
    variant = 'background',
    value,
    width = '100%',
    height = '100%',
    placeholder,
    name,
    isActive,
    resizeValue = 'none',
    onFocus,
    onBlur,
    onChange = () => {},
    maxLength,
    showcaseLength,
    maxLengthPosition = 'right',
    error,
    disabled = false,
    rows,
    borderColor,
    style,
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event, { id: name, value: event.currentTarget.value });
  };

  const [content, setContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const cursorPositionRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (textAreaRef.current && cursorPositionRef.current !== null) {
      textAreaRef.current.selectionStart = cursorPositionRef.current.selectionStart;
      textAreaRef.current.selectionEnd = cursorPositionRef.current.selectionEnd;
    }
  }, [content]);

  const handleTextAreaChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setContent((event.target as HTMLTextAreaElement).value);
    setIsEditing(true);
    if (cursorPositionRef && cursorPositionRef.current) {
      cursorPositionRef.current.selectionStart = (event.target as HTMLTextAreaElement).selectionStart;
    }
  };

  useEffect(() => {
    if (value) {
      setContent(value);
    } else {
      setContent('');
    }
  }, [value]);

  // white space validation
  useEffect(() => {
    if (isEditing) {
      const val = content.trim();
      if (val === '') {
        setContent('');
      }
    }
  }, [content, isEditing]);

  return (
    <>
      <textarea
        id={id || `textarea-${name}`}
        ref={textAreaRef}
        name={name}
        style={{ ...style, width, height, resize: resizeValue, borderColor }}
        className={[styles.textArea, styles[variant], styles[getErrorClass()], styles[getActiveClass()]].join(' ')}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => {
          handleTextAreaChange(e);
          handleChange(e);
        }}
        maxLength={maxLength}
        value={content}
        disabled={disabled}
        rows={rows}
      />
      <ErrorText error={error} size="sm" indentation />
      {showcaseLength && (
        <Box jc={maxLengthPosition === 'left' ? 'fs' : 'fe'}>
          <span className={styles.lengthContainer}>
            {content?.length}/{maxLength}
          </span>
        </Box>
      )}
    </>
  );
  function getErrorClass() {
    return error ? 'hasError' : '';
  }
  function getActiveClass() {
    return isActive ? 'active' : '';
  }
};

export default TextArea;
